import React from "react";
import { graphql } from "gatsby";
import { motion } from "framer-motion";
import { PrismicRichText } from "@prismicio/react";
import { GatsbyImage } from "gatsby-plugin-image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import * as styles from "./press.module.css";
import Icon from "../../components/Icon";
import VisuallyHidden from "../../components/VisuallyHidden";

import "swiper/css";
import "swiper/css/pagination";

export const Press = ({ slice }) => {
  const { heading, copy } = slice?.primary || {};
  const swiperRef = React.useRef(null);

  return (
    <section
      data-color-scheme="paper"
      data-highlight-background="spice-red"
      data-highlight-foreground="white"
      data-tilt-scheme="left"
      id="press"
    >
      <div className="page-width padding-section-vertical">
        <motion.div
          className={styles.header}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {heading && (
            <PrismicRichText
              field={heading.richText}
              components={{
                heading2: ({ children }) => (
                  <h2 className="h2 text-center">{children}</h2>
                ),
              }}
            />
          )}
          {copy && <PrismicRichText field={copy.richText} />}
        </motion.div>

        <motion.div
          className={styles.carouselWrapper}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <Swiper
            modules={[Navigation, Pagination]}
            onBeforeInit={swiper => {
              swiperRef.current = swiper;
            }}
            spaceBetween={24}
            slidesPerView={1}
            pagination={{ clickable: true, el: `.${styles.pagination}` }}
            breakpoints={{
              640: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
            className={styles.swiper}
          >
            {slice.items?.map(
              (
                {
                  press_link,
                  press_logo,
                  featured_image,
                  badge_image,
                  headline,
                  category,
                },
                index
              ) => (
                <SwiperSlide key={index} className={styles.slide}>
                  <PressCard
                    link={press_link}
                    logo={press_logo}
                    image={featured_image}
                    badge={badge_image}
                    headline={headline}
                    category={category}
                  />
                </SwiperSlide>
              )
            )}
          </Swiper>

          <div className={styles.controls}>
            <button
              className={styles.navButton}
              onClick={() => swiperRef.current?.slidePrev()}
              aria-label="Previous slide"
            >
              <Icon name="chevron" size="1.5rem" />
              <VisuallyHidden>Previous</VisuallyHidden>
            </button>
            <div className={styles.pagination} />
            <button
              className={styles.navButton}
              onClick={() => swiperRef.current?.slideNext()}
              aria-label="Next slide"
            >
              <Icon name="chevron" size="1.5rem" />
              <VisuallyHidden>Next</VisuallyHidden>
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Card component for each press/award item
const PressCard = ({ link, logo, image, badge, headline, category }) => {
  const hasRichContent = image?.gatsbyImageData || headline?.text;

  // Rich card with image, headline, etc.
  if (hasRichContent) {
    const CardWrapper = link?.url ? "a" : "div";
    const cardProps = link?.url
      ? { href: link.url, target: "_blank", rel: "noreferrer" }
      : {};

    return (
      <CardWrapper className={styles.card} {...cardProps}>
        {image?.gatsbyImageData && (
          <div className={styles.cardImageWrapper}>
            <GatsbyImage
              image={image.gatsbyImageData}
              alt={image.alt || ""}
              className={styles.cardImage}
            />
            {badge?.gatsbyImageData && (
              <div className={styles.badge}>
                <GatsbyImage
                  image={badge.gatsbyImageData}
                  alt={badge.alt || ""}
                  className={styles.badgeImage}
                />
              </div>
            )}
          </div>
        )}
        <div className={styles.cardContent}>
          {category?.text && <p className={styles.cardMeta}>{category.text}</p>}
          {headline?.text && (
            <h3 className={styles.cardHeadline}>{headline.text}</h3>
          )}
          {link?.url && (
            <span className={styles.cardLink}>
              Learn more <Icon name="external" size=".8em" />
            </span>
          )}
        </div>
      </CardWrapper>
    );
  }

  // Fallback: Simple logo card (backward compatible)
  if (logo?.gatsbyImageData) {
    const CardWrapper = link?.url ? "a" : "div";
    const cardProps = link?.url
      ? { href: link.url, target: "_blank", rel: "noreferrer" }
      : {};

    return (
      <CardWrapper className={styles.logoCard} {...cardProps}>
        <GatsbyImage
          image={logo.gatsbyImageData}
          alt={logo.alt || ""}
          objectFit="contain"
          className={styles.logo}
        />
        {link?.url && (
          <span className={styles.pressLink}>
            Read the article <Icon name="external" size=".8em" />
          </span>
        )}
      </CardWrapper>
    );
  }

  return null;
};

export const query = graphql`
  fragment HomepageDataBodyPress on PrismicHomepageDataBodyPress {
    primary {
      heading {
        richText
      }
      copy {
        richText
      }
    }
    items {
      press_link {
        url
      }
      press_logo {
        gatsbyImageData(placeholder: BLURRED)
        url
        alt
      }
      featured_image {
        gatsbyImageData(placeholder: BLURRED, aspectRatio: 1.5)
        alt
      }
      badge_image {
        gatsbyImageData(placeholder: BLURRED, width: 80)
        alt
      }
      headline {
        text
      }
      category {
        text
      }
    }
  }
`;
